import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

/**
 * Records one pageview per route change.
 *
 * Replaces the three hand-rolled `axios.post('/api/analytics/hit')` calls that
 * lived inside Home, CustomDevelopment and OpenSource — every other page was
 * invisible, so traffic to /services, /contact and /about was never counted at
 * all. Mounting this once inside the Router covers every route, including any
 * added later.
 *
 * Consent: only fires when the visitor has actively accepted. `consentGiven` is
 * null while undecided and false on reject, so the strict `=== true` check
 * means no beacon is sent in either case — same rule the old per-page calls
 * used.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  const { consentGiven } = useAuth();
  const lastPath = useRef(null);

  useEffect(() => {
    if (consentGiven !== true) return;

    // React 18 StrictMode double-invokes effects in development; without this
    // guard every pageview would be counted twice locally.
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    // Fire and forget — analytics must never block or break a page render.
    axios
      .post("/api/analytics/hit", { path: location.pathname, type: "pageview" })
      .catch(() => { });
  }, [location.pathname, consentGiven]);

  return null;
};

/**
 * Records a completed goal (e.g. a submitted consultation form).
 * Exported as a plain function so any component can call it on success.
 */
export const trackConversion = (label, path) => {
  axios
    .post("/api/analytics/hit", {
      type: "conversion",
      label,
      path: path || (typeof window !== "undefined" ? window.location.pathname : null),
    })
    .catch(() => { });
};

export default AnalyticsTracker;
